import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

export interface CampaignPaginationProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const CampaignPagination: React.FC<CampaignPaginationProps> = ({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-3 px-1 text-sm text-gray-500">
      {/* Dynamic Summary Count */}
      <div>
        Showing <span className="font-semibold text-gray-800">{startIndex}</span> to{' '}
        <span className="font-semibold text-gray-800">{endIndex}</span> of{' '}
        <span className="font-semibold text-gray-800">{totalItems}</span> campaigns
      </div>

      {/* Pagination Controls - Same pattern as Influencers tab */}
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          {/* Previous Button */}
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>

          {/* Page numbers using exact windowing & button component from InfluencerPagination */}
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className={currentPage === pageNum ? 'bg-purple-600 text-white' : ''}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          {/* Next Button */}
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Page Size Selector */}
        <Select
          value={pageSize.toString()}
          onValueChange={(val) => {
            onPageSizeChange(Number(val));
            onPageChange(1);
          }}
        >
          <SelectTrigger className="w-[110px] h-9 bg-white border-gray-200 text-xs text-gray-700 font-medium rounded-md">
            <SelectValue placeholder={`${pageSize} / Page`} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="5">5 / Page</SelectItem>
            <SelectItem value="10">10 / Page</SelectItem>
            <SelectItem value="20">20 / Page</SelectItem>
            <SelectItem value="50">50 / Page</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

